import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const SUPPORTED_LANGS = ["en", "hi", "kn", "ta", "ml", "te", "bn", "mr", "gu", "other"];
const empty = (cid = "") => ({ category_id: cid, slug: "", sort: 0, active: true, labels: {} });

export default function AdminConcerns() {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());

  const load = async () => {
    const [a, b] = await Promise.all([api.get("/admin/concerns"), api.get("/admin/categories")]);
    setItems(a.data); setCats(b.data);
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing === "new") await api.post("/admin/concerns", form);
      else await api.patch(`/admin/concerns/${editing}`, form);
      toast.success("Saved");
      setEditing(null); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
  };

  return (
    <div className="p-8 lg:p-12 max-w-5xl">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="overline">Funnel</p>
          <h1 className="editorial-h text-5xl text-brand-dark mt-2">Concerns</h1>
        </div>
        <Button onClick={() => { setEditing("new"); setForm(empty(cats[0]?.id || "")); }} data-testid="concern-new-btn" className="rounded-none bg-brand-primary text-brand-bone gap-2">
          <Plus strokeWidth={1.5} className="w-4 h-4" /> Add concern
        </Button>
      </div>

      {editing && (
        <form onSubmit={save} className="mt-8 bg-white border border-brand-line p-6 space-y-4">
          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <Label className="overline">Category</Label>
              <select value={form.category_id} onChange={(e) => setForm({ ...form, category_id: e.target.value })} className="mt-2 w-full h-11 border border-brand-line bg-white px-3 text-sm" required>
                {cats.map((c) => <option key={c.id} value={c.id}>{c.labels?.en || c.slug}</option>)}
              </select>
            </div>
            <div>
              <Label className="overline">Slug</Label>
              <Input className="mt-2 h-11 rounded-none border-brand-line" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} required />
            </div>
            <div>
              <Label className="overline">Sort</Label>
              <Input type="number" className="mt-2 h-11 rounded-none border-brand-line" value={form.sort} onChange={(e) => setForm({ ...form, sort: Number(e.target.value) })} />
            </div>
          </div>
          <div>
            <Label className="overline">Labels (per language)</Label>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 mt-2">
              {SUPPORTED_LANGS.map((l) => (
                <div key={l}>
                  <p className="text-[10px] uppercase tracking-widest text-brand-muted">{l}</p>
                  <Input className="h-10 rounded-none border-brand-line" value={form.labels[l] || ""} onChange={(e) => setForm({ ...form, labels: { ...form.labels, [l]: e.target.value } })} required={l === "en"} />
                </div>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
            <Label className="font-sans text-sm">Active</Label>
          </div>
          <div className="flex gap-3 justify-end">
            <Button type="button" variant="outline" onClick={() => setEditing(null)} className="rounded-none border-brand-line">Cancel</Button>
            <Button type="submit" className="rounded-none bg-brand-primary text-brand-bone">Save</Button>
          </div>
        </form>
      )}

      <div className="mt-8 bg-white border border-brand-line overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-brand-elevated">
            <tr className="text-left">
              <th className="px-4 py-3 overline text-brand-muted">Concern</th>
              <th className="px-4 py-3 overline text-brand-muted">Category</th>
              <th className="px-4 py-3 overline text-brand-muted">Slug</th>
              <th className="px-4 py-3 overline text-brand-muted">Active</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((c) => {
              const cat = cats.find((x) => x.id === c.category_id);
              return (
                <tr key={c.id} className="border-t border-brand-line">
                  <td className="px-4 py-3 font-semibold">{c.labels?.en || c.slug}</td>
                  <td className="px-4 py-3">{cat?.labels?.en || "—"}</td>
                  <td className="px-4 py-3 font-mono text-xs">{c.slug}</td>
                  <td className="px-4 py-3">{c.active ? "Yes" : "No"}</td>
                  <td className="px-4 py-3 flex gap-2">
                    <button onClick={() => { setEditing(c.id); setForm({ category_id: c.category_id, slug: c.slug, sort: c.sort || 0, active: c.active, labels: c.labels || {} }); }} className="text-brand-dark hover:text-brand-primary"><Pencil strokeWidth={1.5} className="w-4 h-4" /></button>
                    <button onClick={async () => { if (window.confirm("Delete?")) { await api.delete(`/admin/concerns/${c.id}`); load(); } }} className="text-brand-dark hover:text-brand-primary"><Trash2 strokeWidth={1.5} className="w-4 h-4" /></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
